import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PartBayButton from '../PartBayButton';
import { LocationsPicker } from './UsersInlineCells';
import { MakesPicker } from './MakesInlineCells';
import { toLocationList, type LocationList } from './locationListValue';
import { toMakeList, type MakeList, type MakeOption } from './makesListValue';
import type { LocationEntry } from '../../hooks/useGetLocations';

/** Fields the re-quote bulk bar can set across a selection. */
export type RequoteBulkField = 'makes' | 'locations';

export interface RequoteBulkValue {
    makes?: MakeList;
    locations?: LocationList;
}

export interface RequoteBulkEditModalProps {
    field: RequoteBulkField | null;
    selectedCount: number;
    locations: LocationEntry[];
    makeOptions: MakeOption[];
    onApply: (value: RequoteBulkValue) => void;
    onClose: () => void;
}

const FIELD_TITLE: Record<RequoteBulkField, string> = {
    makes: 'Change makes',
    locations: 'Change locations',
};

/**
 * One value, applied to every selected re-quote rule. The bulk bar decides
 * which field is being changed; this only collects the new value and hands it
 * back, matching how the DRP and users bulk editors work.
 */
const RequoteBulkEditModal = ({
    field,
    selectedCount,
    locations,
    makeOptions,
    onApply,
    onClose,
}: RequoteBulkEditModalProps) => {
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>('all');
    const [makeIds, setMakeIds] = useState<Array<string>>([]);

    // Reset between openings so a previous pick never carries over.
    useEffect(() => {
        if (!field) return;
        setLocationIds('all');
        setMakeIds([]);
    }, [field]);

    if (!field) return null;

    // A rule with no makes would never fire, so an empty makes pick is not a
    // change worth applying; locations may legitimately be narrowed to none.
    const canApply = field === 'makes' ? makeIds.length > 0 : true;

    const handleApply = () => {
        if (field === 'makes') onApply({ makes: toMakeList(makeIds, makeOptions) });
        else onApply({ locations: toLocationList(locationIds, locations) });
    };

    return (
        <Modal isOpen onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    {FIELD_TITLE[field]}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Applies to {selectedCount} selected rule
                            {selectedCount === 1 ? '' : 's'}.
                        </Text>

                        {field === 'makes' ? (
                            <MakesPicker
                                value={makeIds}
                                options={makeOptions}
                                onChange={setMakeIds}
                                maxH="260px"
                            />
                        ) : (
                            <LocationsPicker
                                value={locationIds}
                                locations={locations}
                                onChange={setLocationIds}
                                maxH="260px"
                            />
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        isDisabled={!canApply}
                        onClick={handleApply}
                        text="Apply"
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default RequoteBulkEditModal;
